import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import { IDateRange } from '../../../providers/settings/dates/dateRangeProvider';
import { ITrendReportSettings } from '../../../providers/settings/reports/trendReportSettingsProvider';
import { ISettingsTemplate } from '../../../providers/settings/reports/types';
import { userAsPromise } from '../../../providers/userProvider';

/**
 * @description updates the trend template
 * @param metric
 * @returns Promise
 */
export const updateTrendTemplate = async (
  template: ISettingsTemplate<ITrendReportSettings>,
  isSelected: boolean
): Promise<void> => {
  const user = await userAsPromise();

  if (isSelected) {
    const batch = writeBatch(user.services.firestore);
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/trendReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/trendReport`
      ),
      { selected: template },
      { merge: true }
    );
    await batch.commit();
  } else {
    await setDoc(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/trendReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
  }
};

/**
 * @description creates a new trend report settings template
 * @param template
 */

export const createTrendTemplate = async (
  template: Partial<ISettingsTemplate<ITrendReportSettings>>
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/settings/trendReport/templates`
    )
  );
  const newDocument = {
    ...template,
    id: docRef.id,
    active: true,
  };
  await setDoc(docRef, newDocument, { merge: true });
};

/**
 * @description saves the template as the selected template
 * @param template
 */
export const saveTrendSettings = async (
  template: ISettingsTemplate<ITrendReportSettings>,
  timeframe: IDateRange,
  whiskers: boolean
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/trendReport`
  );

  await setDoc(
    docRef,
    {
      selected: template ?? null,
      timeframe: timeframe ?? null,
      whiskers: whiskers ?? null,
    },
    { merge: true }
  );
};

/**
 * @description delete the template and change the current template to another one
 * @param template
 * @param replacement
 * @returns
 */
export const deleteSelectedTemplate = async (
  template: ISettingsTemplate<ITrendReportSettings>,
  replacement: ISettingsTemplate<ITrendReportSettings>
): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);
  const updateRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/trendReport/templates/${template.id}`
  );
  const update: ISettingsTemplate<ITrendReportSettings> = {
    ...template,
    active: false,
  };
  batch.update(updateRef, update as unknown as Record<string, unknown>);
  const selectedRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/trendReport`
  );
  batch.set(selectedRef, { selected: replacement ?? null }, { merge: true });
  return batch.commit();
};
